import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { Account } from '@/client/accounts';
import {
  CourseContent,
  CourseFormat,
  CourseFormState,
  CourseStatus,
  MaterialType,
} from '@/client/courses/types';
import { File } from '@/client/files';
import { Tag } from '@/client/tags/types/tags.type';
import { ReplaceScheduledContentBanner } from '@/components/courses/customization/ReplaceScheduledContentBanner';
import { ContentForm } from '@/components/courses/forms/ContentForm';
import { useCourse, useUpdateCourse } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui/styled-ui';
import {
  generateCourseTranslationsInitialValues,
  getContentItemId,
  modifyCourseFiles,
  modifyCourseTranslations,
  parseModifiedCourseFiles,
  parseModifiedCourseTranslations,
  queryStateConverter,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FormikErrors, FormikValues } from 'formik';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

export const CourseContentPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { id: courseId } = useParams();
  const { course, isLoading, refetch } = useCourse({
    courseId: courseId,
    staleTime: Infinity,
  });

  const [formInitialValues, setFormInitialValues] = useState<CourseFormState>({
    courseTranslations: generateCourseTranslationsInitialValues(),
    courseLanguages: [],
    courseThumbnail: '',
    courseFiles: modifyCourseFiles([
      {
        language: LanguagesEnum.EN,
        file: null,
      },
    ]),
    courseContents:
      course?.courseContents?.sort(
        (a, b) => (a.position || 0) - (b.position || 0),
      ) || [],
    format: CourseFormat.CONTENTS,
    courseCreatedAt: undefined,
    courseLastUpdatedAt: undefined,
    type: MaterialType.COURSE,
    isStandardCatalogue: false,
    releaseDate: null,
    plannerScheduleDate: null,
    retired: false,
    customContent: false,
    customAccount: null,
    selfHosted: true,
    hubspotProperty: undefined,
    hubspotDateProperty: undefined,
    priority: null,
    label: undefined,
    isPreviewable: false,
    exemptFromMinimumRequirementScore: false,
    isCloneable: false,
    isAiCustomizable: false,
    tags: [],
    contentSeries: undefined,
  });

  const [courseThumbnailsData, setCourseThumbnailData] = useState<File>(
    {} as File,
  );
  const [customAccountData, setCustomAccountData] = useState<Account>(
    {} as Account,
  );

  useEffect(() => {
    if (!course) return;

    setFormInitialValues({
      id: course.id,
      courseTranslations: modifyCourseTranslations(course.courseTranslations),
      courseLanguages: course?.courseLanguages || [],
      courseThumbnail: course.courseThumbnail ? course.courseThumbnail.id : '',
      courseFiles: modifyCourseFiles(course.courseFiles),
      courseContents: course.courseContents || [],
      format: course?.format,
      courseCreatedAt: course.courseCreatedAt
        ? new Date(course.courseCreatedAt)
        : undefined,
      courseLastUpdatedAt: course.courseLastUpdatedAt
        ? new Date(course.courseLastUpdatedAt)
        : undefined,
      type: course.type,
      isStandardCatalogue: !!course.isStandardCatalogue,
      releaseDate: course.releaseDate ? new Date(course.releaseDate) : null,
      plannerScheduleDate: course.plannerScheduleDate
        ? new Date(course.plannerScheduleDate)
        : null,
      retired: course.retired,
      customAccount: course.account ? course.account.id : null,
      customContent: !!course.account,
      selfHosted: course?.selfHosted,
      customizationMetadata: course?.customizationMetadata,
      hubspotProperty: course?.hubspotProperties?.hubspotProperty,
      hubspotDateProperty: course?.hubspotProperties?.hubspotDateProperty,
      priority: course?.priority,
      label: course?.label ? course.label : undefined,
      defaultLanguage: course.defaultLanguage,
      isPreviewable:
        !!course.isPreviewable &&
        moment(course?.releaseDate).isAfter(moment(), 'day'),
      exemptFromMinimumRequirementScore:
        course.exemptFromMinimumRequirementScore,
      isCloneable: !!course?.cloneSettings?.isCloneable,
      isAiCustomizable: !!course?.aiCustomizationSettings?.isEnabled,
      cloneMetadata: course?.cloneMetadata,
      tags: course?.tags,
      contentSeries: course?.contentSeries,
    });

    if (course.courseThumbnail) {
      setCourseThumbnailData(course.courseThumbnail);
    }

    if (course.account) {
      setCustomAccountData(course.account);
    }

    if (course) {
      setSelfHosted(course.selfHosted);
    }
  }, [course]);

  const updateCourse = useUpdateCourse();

  //handling courses with status draft that was not deleted by clicking cancel or navigating away
  useEffect(() => {
    if (courseId && course?.status === CourseStatus.DRAFT && course?.format) {
      const timer = setTimeout(() => {
        updateCourse
          .update({
            courseId,
            updates: { status: CourseStatus.SAVED, format: course.format },
          })
          .then(() => {
            refetch();
          })
          .catch(() => {
            toast?.error(
              t('error.course.saveDraftFailed'),
              t('error.course.saveDraftFailed'),
            );
          });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [courseId, refetch]);

  const handleSubmit = async (data: FormikValues) => {
    try {
      if (!currentAccount || !courseId) return;

      await updateCourse.update({
        updates: {
          courseTranslations: parseModifiedCourseTranslations(
            data.courseTranslations,
          ),
          courseLanguages: data.courseLanguages,
          courseThumbnail: data.courseThumbnail ? data.courseThumbnail : null,
          courseFiles: parseModifiedCourseFiles(data.courseFiles, {
            selfHosted: data.selfHosted,
            courseLanguages: data.courseLanguages,
          }),
          format: CourseFormat.CONTENTS,
          courseContents: data.courseContents.map(
            (item: CourseContent, index: number) => ({
              ...item,
              id: item.id?.includes('temp') ? undefined : item.id,
              position: index,
              quiz: getContentItemId(item.quiz),
              resource: getContentItemId(item.resource),
              presentation: getContentItemId(item.presentation),
            }),
          ),
          courseCreatedAt: data.courseCreatedAt ? data.courseCreatedAt : null,
          courseLastUpdatedAt: data.courseLastUpdatedAt
            ? data.courseLastUpdatedAt
            : null,
          type: data.type,
          isStandardCatalogue: data.isStandardCatalogue,
          releaseDate: data.isStandardCatalogue ? data.releaseDate : null,
          plannerScheduleDate: data.plannerScheduleDate
            ? data.plannerScheduleDate
            : null,
          account:
            data.customContent && !!data.customAccount
              ? data.customAccount
              : null,
          selfHosted: data.selfHosted,
          hubspotProperty: data.hubspotProperty,
          hubspotDateProperty: data.hubspotDateProperty,
          priority: data.priority,
          label: data?.label ? data.label : null,
          isPreviewable:
            moment(course?.releaseDate).isAfter(moment(), 'day') &&
            !!data?.isPreviewable,
          exemptFromMinimumRequirementScore:
            data.exemptFromMinimumRequirementScore,
          isCloneable: data.isCloneable,
          isAiCustomizable: data.isAiCustomizable,
          status: CourseStatus.SAVED,
          tags: data?.tags?.map((tag: Tag) => tag.id),
          contentSeries: data?.contentSeries ?? null,
        },
        courseId: courseId,
      });

      toast?.success(t('toast.success'), t('content.updated'));
      refetch();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const [selfHosted, setSelfHosted] = useState<boolean>();

  const handleChangeCourseType = (selfHosted: boolean) => {
    setSelfHosted(selfHosted);
    setFormInitialValues({
      ...formInitialValues,
      selfHosted,
      courseFiles: [],
      courseLanguages: selfHosted ? [LanguagesEnum.EN] : [],
    });
  };

  const submitFormRef = useRef<(() => Promise<void>) | null>(null);
  const [formErrors, setFormErrors] =
    useState<FormikErrors<CourseFormState> | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const onUnsavedChangesCheck = (hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  };

  return (
    <>
      {isLoading && !course && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>{t('content.details.loading')}</h3>
        </FlexContainer>
      )}
      {!isLoading && course && (
        <>
          <CoursesActionHeader
            course={course}
            selfHosted={selfHosted}
            onChangeCourseType={handleChangeCourseType}
            onTriggerRefetch={refetch}
          />
          {course && (
            <CoursesTabs
              course={course}
              hasUnsavedChanges={hasUnsavedChanges}
              hasErrors={!!Object.keys(formErrors || {}).length}
              onSave={async () => {
                if (submitFormRef.current) {
                  await submitFormRef.current();
                }
              }}
            />
          )}
          {currentAccount && course && (
            <ReplaceScheduledContentBanner
              accountId={currentAccount.id}
              accountName={currentAccount.name}
              course={course}
            />
          )}
          <FlexContainer justify="flex-start">
            <ContentForm
              initialValues={formInitialValues}
              selfHosted={selfHosted}
              state={queryStateConverter(updateCourse)}
              onSubmit={handleSubmit}
              courseThumbnailData={courseThumbnailsData}
              customAccountData={customAccountData}
              defaultLanguage={course.defaultLanguage}
              onUnsavedChangesCheck={onUnsavedChangesCheck}
              submitFormRef={submitFormRef}
              onErrorsChange={setFormErrors}
              course={course}
            />
          </FlexContainer>
        </>
      )}
    </>
  );
};
